import { useMemo } from 'react';

import { clone, concat, find, get, isNil, keyBy, reduce, sortBy } from 'lodash';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';

import type {
  GetTransformedRecipientsResponse,
  RosterAdultMember,
  RosterYouthMember,
  UnitMemberPaymentLogBalance,
} from '@appTypes/esb';
import type {
  Member,
  Recipient,
  Recipients,
} from '@appTypes/esb/organizations/v2/_orgGuid/recipients';
import type { PositionElement } from '@appTypes/esb/organizations/v2/units/_orgGuid/youths';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { useGetMeritBadgeCounselorProfileQuery } from '@modules/meritBadgeCounselors/api';
import { useGetRecipientsQuery } from '@modules/messaging/api';
import {
  useGetOrgAdultsQuery,
  useGetOrgYouthsQuery,
  useGetUnitRosterAdultsQuery,
  useGetUnitRosterYouthsQuery,
} from '@modules/roster/api';
import { useGetMembersPaymentLogsBalanceQuery } from '@paymentLogs/api';
import { MINIMUM_DATE } from '@paymentLogs/constants';
import { organizationGuidSel } from '@shared/duck/selectors/general.selectors';
import { intl } from '@shared/localization';
import { getPreferredName } from '@shared/utils/preferredName';
import transformRecipients from '@shared/utils/transformRecipients';
import { currentUserMIDSel } from '@user/duck/selectorsTyped';

import type { ParentDict, YouthDict } from './types';
import { transformYouthsToRecipients } from './utils';

function getUsersSubunits(
  users: RosterYouthMember[] | RosterAdultMember[],
  user: Recipient,
) {
  const match = find(
    users,
    (item: Member) => item.memberId === user.memberId,
  ) as RosterYouthMember;
  let subunits: string[] = [];
  let userId: number | undefined = undefined;

  if (match) {
    subunits = reduce(
      get(match, 'positions', []),
      (positionMemo: string[], position: PositionElement) => {
        if (position.patrolName) {
          positionMemo.push(
            intl.formatMessage(
              {
                id: 'messaging.ManageMembersModal.patrol',
              },
              { patrol: position.patrolName },
            ),
          );
        } else if (position.denNumber) {
          positionMemo.push(
            intl.formatMessage(
              {
                id: 'messaging.ManageMembersModal.den',
              },
              { den: position.denNumber },
            ),
          );
        }
        return positionMemo;
      },
      [],
    );
    userId = match.userId;
  }

  return { subunits, match, userId };
}

export function useMessageRecipients() {
  const organizationGuid = useSelector(organizationGuidSel);
  const {
    isFetching: loadingRecipients,
    data: recipients = {
      leaders: [],
      youths: [],
      parents: [],
      participants: [],
    },
  } = useGetRecipientsQuery(
    {
      organizationGuid,
    },
    {
      skip: !organizationGuid,
    },
  );
  const {
    isFetching: loadingRosterYouths,
    data: rosterYouths = { users: [] },
  } = useGetUnitRosterYouthsQuery(
    {
      organizationGuid,
    },
    {
      skip: !organizationGuid,
    },
  );
  const {
    isFetching: loadingRosterAdults,
    data: rosterAdults = { users: [] },
  } = useGetUnitRosterAdultsQuery(
    {
      organizationGuid,
    },
    {
      skip: !organizationGuid,
    },
  );

  const { data: adultData } = useGetOrgAdultsQuery(
    {
      organizationGuid,
      body: { includeRegistrationDetails: true, includeExpired: true },
    },
    {
      skip: !organizationGuid,
    },
  );

  const { data: youthData } = useGetOrgYouthsQuery(
    {
      organizationGuid,
      body: { includeRegistrationDetails: true, includeExpired: true },
    },
    {
      skip: !organizationGuid,
    },
  );

  const adultDict = keyBy(adultData?.members, 'memberId');
  const youthDict = keyBy(youthData?.members, 'memberId');
  const isOptOutOverrideEnabled = featureFlags.getFlag(
    'SBL_6779_OPT_OUT_OVERRIDE_MESSAGING',
  );

  const isLoading =
    loadingRecipients || loadingRosterYouths || loadingRosterAdults;

  const transformedRecipients: GetTransformedRecipientsResponse = useMemo(
    () => ({
      isLoading,
      ...transformRecipients(
        youthData,
        reduce(
          recipients,
          (memo: Recipients, list, category) => {
            if (category === 'leaders') {
              memo.leaders = reduce(
                list,
                (submemo: Recipient[], leader) => {
                  const { subunits: youthSubunits, match } = getUsersSubunits(
                    get(rosterYouths, 'users', []),
                    leader,
                  );
                  const { subunits: adultSubunits, match: adultMatch } =
                    getUsersSubunits(get(rosterAdults, 'users', []), leader);
                  const subunits: string[] = concat(
                    youthSubunits,
                    adultSubunits,
                  );
                  const firstName = getPreferredName({
                    nickName: get(
                      match,
                      'nickName',
                      get(adultMatch, 'nickName'),
                    ),
                    firstName: leader.firstName,
                  });
                  if (match) {
                    if (get(match, 'age', 0) >= 18) {
                      if (!memo.participants) {
                        memo.participants = [];
                      }
                      memo.participants.push({
                        ...leader,
                        noEmails: leader.noEmails,
                        optedOut:
                          adultDict?.[leader.memberId]?.registrarInfo
                            ?.isAutoRenewalOptedOut,
                        firstName,
                        subunits,
                      });
                    }
                  } else {
                    submemo.push({
                      ...leader,
                      noEmails: leader.noEmails,
                      optedOut:
                        adultDict?.[leader.memberId]?.registrarInfo
                          ?.isAutoRenewalOptedOut,
                      firstName,
                      subunits,
                    });
                  }
                  return submemo;
                },
                [],
              );
            } else if (category === 'youths') {
              memo.youths = reduce(
                list,
                (submemo: Recipient[], youth) => {
                  const { subunits, match, userId } = getUsersSubunits(
                    get(rosterYouths, 'users', []),
                    youth,
                  );
                  const firstName = getPreferredName({
                    nickName: get(match, 'nickName'),
                    firstName: youth.firstName,
                  });
                  submemo.push({
                    ...youth,
                    userId,
                    noEmails: youth.noEmails,
                    optedOut:
                      youthDict?.[youth.memberId]?.registrarInfo
                        ?.isAutoRenewalOptedOut,
                    firstName,
                    subunits,
                  });
                  return submemo;
                },
                [],
              );
            } else if (category === 'parents') {
              memo.parents = reduce(
                list,
                (submemo: Recipient[], parent) => {
                  const { subunits, match } = getUsersSubunits(
                    get(rosterAdults, 'users', []),
                    parent,
                  );
                  const firstName = getPreferredName({
                    nickName: get(match, 'nickName'),
                    firstName: parent.firstName,
                  });
                  submemo.push({
                    ...parent,
                    noEmails: parent.noEmails,
                    optedOut:
                      isOptOutOverrideEnabled && parent.youths?.length
                        ? false
                        : adultDict?.[parent.memberId]?.registrarInfo
                            ?.isAutoRenewalOptedOut,
                    firstName,
                    subunits,
                  });
                  return submemo;
                },
                [],
              );
            }
            return memo;
          },
          clone(recipients),
        ),
      ),
    }),
    [
      recipients,
      rosterYouths,
      rosterAdults,
      isLoading,
      adultDict,
      youthDict,
      youthData,
      isOptOutOverrideEnabled,
    ],
  );

  return {
    isLoading,
    recipientsList: transformedRecipients,
  };
}

export function useBalanceMessageRecipients() {
  const organizationGuid = useSelector(organizationGuidSel);
  const { recipientsList, isLoading } = useMessageRecipients();

  const { isLoading: isMembersPaymentBalanceLoading, data } =
    useGetMembersPaymentLogsBalanceQuery({
      orgGuid: organizationGuid,
      params: {
        fromDate: MINIMUM_DATE,
        toDate: DateTime.now().toFormat('yyyy-MM-dd'),
      },
    });

  if (isNil(data)) {
    const emptyAdults = recipientsList.adults.map(member => ({
      ...member,
      balance: 0,
    }));
    const emptyYouths = recipientsList.youth.map(member => ({
      ...member,
      balance: 0,
    }));
    return {
      adults: emptyAdults,
      youths: emptyYouths,
      adultsDict: {},
      youthsDict: {} as YouthDict,
      parentsDict: {} as ParentDict,
      subunits: recipientsList.subunits,
      isLoading: isLoading || isMembersPaymentBalanceLoading,
    };
  }

  // Remove the unit self balance
  const userPaymentData =
    data?.reduce<Record<string, UnitMemberPaymentLogBalance>>(
      (curr, paymentLogMember) => {
        if (!paymentLogMember.userId) return curr;
        return { ...curr, [`${paymentLogMember.memberId}`]: paymentLogMember };
      },
      {},
    ) ?? {};

  const adultsWithBalance = recipientsList.adults.map(member => {
    const paymentMember = userPaymentData[member.memberId];
    return {
      ...member,
      balance: paymentMember?.balanceDetails?.balance ?? 0,
    };
  });

  const youthsWithBalance = recipientsList.youth.map(member => {
    const paymentMember = userPaymentData[member.memberId];
    return {
      ...member,
      balance: paymentMember?.balanceDetails?.balance ?? 0,
    };
  });
  const adultsDict = keyBy(adultsWithBalance, 'memberId');
  const youthsDict = keyBy(youthsWithBalance, 'memberId');

  // Parents of youth with non zero balance but no email, so they recieve emails
  const parentsDict = Object.values(youthsDict).reduce((acc, member) => {
    member.memberIdsToAdd.forEach(idToAdd => {
      const entry = {
        userId: member.userId,
        firstName: member.firstName,
        lastName: member.lastName,
        balance: member.balance,
      };
      if (acc[idToAdd]) {
        acc[idToAdd].push(entry);
      } else {
        acc[idToAdd] = [entry];
      }
    });
    return acc;
  }, {} as ParentDict);

  return {
    adults: adultsWithBalance,
    youths: youthsWithBalance,
    adultsDict,
    youthsDict,
    parentsDict,
    subunits: recipientsList.subunits,
    isLoading: isLoading || isMembersPaymentBalanceLoading,
  };
}

export function useMbcMessageRecipients() {
  const memberId = useSelector(currentUserMIDSel);

  const { data: mbcProfile, isLoading: isLoadingMbcProfile } =
    useGetMeritBadgeCounselorProfileQuery(
      { memberId, params: { youthEmailFlag: true } },
      { skip: isNil(memberId) },
    );

  const youths = useMemo(() => {
    if (isNil(mbcProfile)) return [];

    return sortBy(transformYouthsToRecipients(mbcProfile.youths), 'lastName');
  }, [mbcProfile]);

  const counselingBadges = useMemo(
    () => sortBy(mbcProfile?.meritBadges, 'meritBadgeName') ?? [],
    [mbcProfile],
  );

  return {
    mbcProfile,
    youths,
    counselingBadges,
    isLoadingMbcProfile,
  };
}
